import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';

import { AppRoutes, THROTTLE_LIMIT } from '@/constants';
import { formatDate } from '@/helpers';
import { useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';

import { CloseIcon, Expand01Icon, Loading02Icon, RefreshCcw01Icon } from '@/assets/icons';
import {
  Button,
  Loading,
  MultiSelect,
  NotFoundResult,
  Pagination,
  Search,
  Separator,
  SortDirection,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common';
import { Alert } from '@/lib/ui';

import { PROCESSING_STATUS_OPTIONS } from '@/features/regulatory-intake/constants';
import { Classification, JobStatus, ProcessingStatus } from '@/features/regulatory-intake/enums';
import {
  getClassificationLabel,
  getProcessingStatusIcon,
  getProcessingStatusLabel,
  getProcessingStatusVariant,
} from '@/features/regulatory-intake/utils';

import { LINK_STATUSES } from '@/features/regulatory-intake/intake-details/constants';
import { NotificationField } from '@/features/regulatory-intake/intake-details/enums';
import {
  useMessageReclassifyMutation,
  useMessageResendAllMutation,
  useMessageResendMutation,
  useMessagesSearching,
} from '@/features/regulatory-intake/intake-details/hooks';
import {
  IMessage,
  IMessageSearchingPayload,
} from '@/features/regulatory-intake/intake-details/interfaces';
import { getLinkStatusIcon } from '@/features/regulatory-intake/intake-details/utils';

import { mergeClasses } from '@/utils';
import ManualUploadModal from './ManualUploadModal';
import { ProcessingLogDialog } from './ProcessingLogDialog';

interface IMessagesSection {
  jobId: string;
  jobStatus: JobStatus;
  onJobStatusChange: (status: JobStatus) => void;
  refetchJob: () => void;
}

const MessagesSection = ({ jobId, jobStatus, onJobStatusChange, refetchJob }: IMessagesSection) => {
  const [showManualUpload, setShowManualUpload] = useState<boolean>(false);
  const [selectedEmailId, setSelectedEmailId] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [processingStatuses, setProcessingStatuses] = useState<string[]>([]);
  const [showProcessingLogDialog, setShowProcessingLogDialog] = useState<boolean>(false);
  const [selectedLog, setSelectedLog] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState<string>(NotificationField.Id);
  const [isDescending, setIsDescending] = useState<boolean>(true);
  const previousFilterKeyRef = useRef<string | null>(null);

  // Handle column sorting
  const handleSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setIsDescending(true);
    } else {
      setSortBy(columnKey);
      setIsDescending(direction === 'desc');
    }
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery);

  // Throttle filters and sorting
  const throttledStatuses = useThrottle(processingStatuses, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: IMessageSearchingPayload = {
      jobId: jobId,
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    const processingStatusesCount = Object.keys(ProcessingStatus).length / 2; // Divide by 2 because of how enums are compiled in TS
    if (
      throttledStatuses &&
      throttledStatuses.length > 0 &&
      throttledStatuses.length < processingStatusesCount
    ) {
      payload.statuses = throttledStatuses.map((status) => Number(status) as ProcessingStatus);
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    return payload;
  }, [
    jobId,
    debouncedSearchQuery,
    currentPage,
    throttledStatuses,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { data, isFetching, isError, refetch } = useMessagesSearching(apiPayload);
  const { mutateAsync: reclassifyMessage, isPending: isReclassifyingMessage } =
    useMessageReclassifyMutation();
  const { mutateAsync: resendFailedMessages, isPending: isResendingFailedMessages } =
    useMessageResendAllMutation(jobId);
  const { mutateAsync: resendFailedMessage, isPending: isResendingFailedMessage } =
    useMessageResendMutation();

  const messages = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata?.totalPages || 0, [data?.metadata?.totalPages]);
  const totalItems = useMemo(() => data?.metadata?.totalItems || 0, [data?.metadata?.totalItems]);

  const isFiltered = useMemo(() => processingStatuses.length > 0, [processingStatuses]);
  const isEmpty = useMemo(() => messages.length === 0, [messages]);
  const hasFailedMessages = useMemo(
    () => messages.some((message) => message.processingStatus === ProcessingStatus.Failed),
    [messages],
  );

  const onReclassifyMessage = async (emailId: string, messageId: string) => {
    setSelectedMessageId(messageId);

    await reclassifyMessage(emailId, {
      onSuccess: () => {
        refetch();
      },
      onSettled: () => {
        setSelectedMessageId(null);
      },
    });
  };

  const onResendFailedMessage = async (emailId: string, messageId: string) => {
    setSelectedMessageId(messageId);
    onJobStatusChange(JobStatus.Running);

    await resendFailedMessage(emailId, {
      onSuccess: () => {
        Alert.success('Retrying failed message(s) successfully.');
      },
      onSettled: () => {
        refetch();
        refetchJob();
        setSelectedMessageId(null);
      },
    });
  };

  const onResendFailedMessages = async () => {
    onJobStatusChange(JobStatus.Running);

    await resendFailedMessages(undefined, {
      onSuccess: () => {
        Alert.success('Retrying failed message(s) successfully.');
      },
      onSettled: () => {
        refetch();
        refetchJob();
      },
    });
  };

  const onClearFilters = () => {
    setSearchQuery('');
    setProcessingStatuses([]);
  };

  const renderAction = (message: IMessage) => {
    if (message.processingStatus === ProcessingStatus.Failed) {
      return (
        <Button
          className="min-w-20"
          buttonStyle="outline"
          buttonColor="primary"
          size="sm"
          borderRadius="soft"
          onClick={() => onResendFailedMessage(message.emailId || '', message.id || '')}
          disabled={isResendingFailedMessage || isResendingFailedMessages}
        >
          {(selectedMessageId === message.id && isResendingFailedMessage) ||
          isResendingFailedMessages ? (
            <Loading02Icon className="size-4 text-neutral-6 animate-[spin_2s_linear_infinite]" />
          ) : (
            'Retry'
          )}
        </Button>
      );
    }
    if (message.processingStatus === ProcessingStatus.Attention) {
      return (
        <Button
          className="min-w-20"
          buttonStyle="outline"
          buttonColor="neutral"
          size="sm"
          borderRadius="soft"
          onClick={() => {
            setShowManualUpload(true);
            setSelectedEmailId(message.emailId || '');
          }}
          disabled={isResendingFailedMessage || isResendingFailedMessages}
        >
          Upload
        </Button>
      );
    }
    if (message.classification === Classification.NotRegulatory) {
      return (
        <Button
          buttonStyle="filled"
          buttonColor="neutral"
          size="sm"
          borderRadius="soft"
          className="min-w-20"
          onClick={() => onReclassifyMessage(message.emailId || '', message.id || '')}
          disabled={isReclassifyingMessage || isResendingFailedMessage || isResendingFailedMessages}
        >
          {selectedMessageId === message.id && isReclassifyingMessage ? (
            <Loading02Icon className="size-4 text-neutral-6 animate-[spin_2s_linear_infinite]" />
          ) : (
            'Reclassify'
          )}
        </Button>
      );
    }
  };

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledStatuses,
      throttledSortBy,
      throttledIsDescending,
    ]);

    if (previousFilterKeyRef.current === null) {
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, throttledStatuses, throttledSortBy, throttledIsDescending]);

  if (isError) {
    navigate(AppRoutes.INTERNAL_SERVER_ERROR);
  }

  return (
    <>
      <div className="flex justify-between items-center py-5">
        <div className="text-h6 font-bold text-neutral-9">Messages</div>
        {hasFailedMessages && (
          <Button
            buttonColor="primary"
            buttonStyle="filled"
            borderRadius="soft"
            prefixIcon={
              <SvgIcon
                component={isResendingFailedMessages ? Loading02Icon : RefreshCcw01Icon}
                className={mergeClasses(
                  'size-6',
                  isResendingFailedMessages && 'animate-[spin_2s_linear_infinite]',
                )}
              />
            }
            onClick={() => onResendFailedMessages()}
            disabled={isResendingFailedMessages}
          >
            {isResendingFailedMessages ? 'Retrying All Failed...' : 'Retry All Failed'}
          </Button>
        )}
      </div>
      <div className="flex justify-between gap-4">
        <div className="flex flex-wrap justify-start gap-2 items-center">
          <Search
            placeholder="Search by email subject"
            className="!w-[240px] xl:!w-[362px]"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MultiSelect
            options={PROCESSING_STATUS_OPTIONS}
            placeholder="Processing status"
            defaultValue={processingStatuses}
            onValueChange={(values) => {
              setProcessingStatuses(values);
            }}
            maxCount={1}
            className="!w-fit !min-w-[280px] xl:!min-w-[330px]"
          />
          {isFiltered && (
            <Button
              buttonColor="primary"
              buttonStyle="textlink"
              prefixIcon={<CloseIcon className="w-5 h-5" />}
              onClick={onClearFilters}
              className="gap-1"
            >
              Clear
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center px-3">
          <div className="text-body-xs text-neutral-9 font-semibold">Link status:</div>
          {LINK_STATUSES.map((status, index) => (
            <Fragment key={index}>
              <div className="flex gap-2 items-center">
                {status.icon}
                <div className="text-body-xs text-neutral-7 font-medium">{status.label}</div>
              </div>
              {index !== LINK_STATUSES.length - 1 && (
                <Separator orientation="vertical" className="w-px h-4 bg-neutral-5" />
              )}
            </Fragment>
          ))}
        </div>
      </div>
      {isEmpty && !isFetching ? (
        <NotFoundResult label="No data." />
      ) : (
        <>
          <div className="w-full my-3 pb-3 overflow-auto custom-scrollbar">
            <Table className="!w-fit min-w-full !overflow-hidden" size="sm" template="zebra">
              <TableHeader>
                <TableRow>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === NotificationField.Id ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort(NotificationField.Id, direction)}
                  >
                    S/N
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === NotificationField.Timestamp
                        ? isDescending
                          ? 'desc'
                          : 'asc'
                        : null
                    }
                    onSort={(direction) => handleSort(NotificationField.Timestamp, direction)}
                  >
                    Received Time
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === NotificationField.Subject ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort(NotificationField.Subject, direction)}
                  >
                    Subject
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === NotificationField.Classification
                        ? isDescending
                          ? 'desc'
                          : 'asc'
                        : null
                    }
                    onSort={(direction) => handleSort(NotificationField.Classification, direction)}
                  >
                    Classification
                  </TableHead>
                  <TableHead>Links Extracted</TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === NotificationField.ProcessingStatus
                        ? isDescending
                          ? 'desc'
                          : 'asc'
                        : null
                    }
                    onSort={(direction) =>
                      handleSort(NotificationField.ProcessingStatus, direction)
                    }
                  >
                    Processing Status
                  </TableHead>
                  <TableHead>Processing Log</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((message) => (
                  <TableRow key={message.id}>
                    <TableCell className="font-medium">{message.id}</TableCell>
                    <TableCell className="font-medium">
                      <div className="w-[108px] whitespace-normal">
                        {message.timestamp ? (
                          formatDate({
                            date: new Date(message.timestamp),
                            hasTime: true,
                            hasTimezone: true,
                          })
                        ) : (
                          <span className="block tracking-[1px]">--</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      <span
                        className="w-[172px] line-clamp-2 whitespace-normal break-words"
                        title={message.subject}
                      >
                        {message.subject || '(no subject)'}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="w-[152px]">
                        {getClassificationLabel(
                          message.classification,
                          message.notRegulatoryReason,
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex flex-col gap-1 w-[194px]">
                        {message.links?.urls && message.links.urls.length > 0
                          ? message.links.urls.map((link, index) => (
                              <div className="flex items-center gap-2 truncate" key={index}>
                                {link.errorMessage ? (
                                  <Tooltip>
                                    <TooltipTrigger className="cursor-default align-middle">
                                      {getLinkStatusIcon(link.status)}
                                    </TooltipTrigger>
                                    <TooltipContent
                                      className="text-body-sm font-medium rounded-xs !max-w-[350px]"
                                      size="lg"
                                      side="bottom"
                                    >
                                      {link.errorMessage}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  getLinkStatusIcon(link.status)
                                )}
                                <Link
                                  to={link.url}
                                  target="_blank"
                                  className="truncate underline text-body-xs text-primary-base"
                                  title={link.url}
                                >
                                  {link.url}
                                </Link>
                              </div>
                            ))
                          : 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell
                      cellType="status"
                      className="min-w-[178px] max-w-[178px]"
                      statusVariant={getProcessingStatusVariant(
                        message.processingStatus === ProcessingStatus.Failed &&
                          (message.id === selectedMessageId || isResendingFailedMessages)
                          ? ProcessingStatus.Transferring
                          : message.processingStatus || ProcessingStatus.NotAvailable,
                      )}
                      statusIcon={(() => {
                        const Icon = getProcessingStatusIcon(
                          message.processingStatus === ProcessingStatus.Failed &&
                            (message.id === selectedMessageId || isResendingFailedMessages)
                            ? ProcessingStatus.Transferring
                            : message.processingStatus || ProcessingStatus.NotAvailable,
                        );
                        return <Icon className="size-3" />;
                      })()}
                    >
                      {getProcessingStatusLabel(
                        message.processingStatus === ProcessingStatus.Failed &&
                          (message.id === selectedMessageId || isResendingFailedMessages)
                          ? ProcessingStatus.Transferring
                          : message.processingStatus || ProcessingStatus.NotAvailable,
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {message.processingLog ? (
                        <div className="flex flex-row items-center gap-2">
                          <span
                            className="w-[172px] line-clamp-2 whitespace-normal break-words"
                            title={message.processingLog}
                          >
                            {message.processingLog}
                          </span>
                          <Tooltip>
                            <TooltipTrigger
                              className="cursor-default align-middle"
                              onClick={() => {
                                setShowProcessingLogDialog(true);
                                setSelectedLog(message?.processingLog || null);
                              }}
                            >
                              <Expand01Icon className="size-4 text-primary-base cursor-pointer" />
                            </TooltipTrigger>
                            <TooltipContent
                              className="text-body-sm font-medium rounded-xs"
                              size="lg"
                              side="bottom"
                            >
                              View more
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      ) : (
                        <span className="block tracking-[1px]">--</span>
                      )}
                    </TableCell>
                    <TableCell className="px-none" align="center" cellType="custom">
                      <div className="w-[104px]">
                        {jobStatus !== JobStatus.Running && renderAction(message)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              className="pt-3 pb-6"
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
          {showManualUpload && selectedEmailId && (
            <ManualUploadModal
              open={showManualUpload}
              onOpenChange={(open) => setShowManualUpload(open)}
              emailId={selectedEmailId}
              onRefetch={refetch}
            />
          )}
          {showProcessingLogDialog && (
            <ProcessingLogDialog
              open={showProcessingLogDialog}
              onOpenChange={(open) => setShowProcessingLogDialog(open)}
              title="Processing Log"
              message={selectedLog || ''}
            />
          )}
        </>
      )}
      {isFetching && <Loading fullScreen />}
    </>
  );
};

export default MessagesSection;
